// BotList.jsx
import React, { useState, useEffect } from "react";
import {
	Grid,
	Typography,
	Box,
	Card,
	CardContent,
	Button,
	Chip,
	CircularProgress,
	Alert,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
} from "@mui/material";
import { Link } from "react-router-dom";
import { getAllBots, requestBotAccess } from "../services/botService";
import { Add, PlayArrow, Stop, Error, Send } from "@mui/icons-material";
import useAuth from "../hooks/useAuth";

function BotList() {
	const [bots, setBots] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [requestDialog, setRequestDialog] = useState(false);
	const [requestReason, setRequestReason] = useState("");
	const [requestLoading, setRequestLoading] = useState(false);
	const { user, isAdmin } = useAuth();

	useEffect(() => {
		fetchBots();
	}, []);

	const fetchBots = async () => {
		try {
			setLoading(true);
			const data = await getAllBots();
			setBots(data);
		} catch (error) {
			console.error("Failed to fetch bots:", error);
			setError(error.response?.data?.message || "Failed to load bots");
		} finally {
			setLoading(false);
		}
	};

	const handleRequestOpen = () => {
		setRequestDialog(true);
	};

	const handleRequestClose = () => {
		setRequestDialog(false);
		setRequestReason("");
	};

	const handleRequestSubmit = async () => {
		if (!requestReason.trim()) {
			return;
		}

		try {
			setRequestLoading(true);
			await requestBotAccess({ reason: requestReason });
			setSuccess("Access request sent to admin. You will be notified when approved.");
			handleRequestClose();
		} catch (error) {
			console.error("Failed to send request:", error);
			setError(error.response?.data?.message || "Failed to send access request");
		} finally {
			setRequestLoading(false);
		}
	};

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
				<Typography variant="h4" fontWeight="bold">
					My Bots
				</Typography>
				{isAdmin() ? (
					// Admin sees Add New Bot button
					<Button variant="contained" color="primary" startIcon={<Add />} component={Link} to="/bots/new">
						Add New Bot
					</Button>
				) : (
					// Regular user sees Request Access button
					<Button variant="contained" color="primary" startIcon={<Send />} onClick={handleRequestOpen}>
						Request Access
					</Button>
				)}
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
					{error}
				</Alert>
			)}

			{success && (
				<Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
					{success}
				</Alert>
			)}

			{bots.length === 0 ? (
				<Card sx={{ borderRadius: 2, p: 4, textAlign: "center" }}>
					<Typography variant="h6" color="text.secondary" gutterBottom>
						You don't have any bots yet
					</Typography>
					<Typography variant="body2" color="text.secondary" paragraph>
						{isAdmin() ? "Create your first Discord bot to get started" : "Request access to a bot or wait for an admin to grant you permissions"}
					</Typography>
					{isAdmin() ? (
						<Button variant="contained" color="primary" startIcon={<Add />} component={Link} to="/bots/new" sx={{ mt: 2 }}>
							Create a Bot
						</Button>
					) : (
						<Button variant="contained" color="primary" startIcon={<Send />} onClick={handleRequestOpen} sx={{ mt: 2 }}>
							Request Access
						</Button>
					)}
				</Card>
			) : (
				<Grid container spacing={3}>
					{bots.map((bot) => (
						<Grid item key={bot.id} xs={12} sm={6} md={4} lg={3}>
							<Card
								sx={{
									borderRadius: 2,
									height: "100%",
									display: "flex",
									flexDirection: "column",
									transition: "transform 0.2s, box-shadow 0.2s",
									"&:hover": {
										transform: "translateY(-5px)",
										boxShadow: 4,
									},
								}}
							>
								<CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
									<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
										<Typography variant="h6" fontWeight="bold" gutterBottom>
											{bot.name}
										</Typography>
										<Chip
											icon={
												bot.status === "running" ? (
													<PlayArrow fontSize="small" />
												) : bot.status === "error" ? (
													<Error fontSize="small" />
												) : (
													<Stop fontSize="small" />
												)
											}
											label={bot.status}
											size="small"
											color={bot.status === "running" ? "success" : bot.status === "error" ? "error" : "default"}
										/>
									</Box>

									<Box sx={{ mb: 2, color: "text.secondary", fontSize: "0.875rem" }}>
										<Typography variant="body2" color="text.secondary" gutterBottom>
											Created: {new Date(bot.created_at).toLocaleDateString()}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Owner: {bot.owner}
										</Typography>
									</Box>

									<Box sx={{ mt: "auto" }}>
										<Button variant="outlined" component={Link} to={`/bots/${bot.id}`} fullWidth>
											View Details
										</Button>
									</Box>
								</CardContent>
							</Card>
						</Grid>
					))}
				</Grid>
			)}

			{/* Request Access Dialog */}
			<Dialog open={requestDialog} onClose={handleRequestClose} maxWidth="sm" fullWidth>
				<DialogTitle>Request Bot Access</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
						Send a request to admins to get access to Discord bots. Please explain why you need access and which permissions you require.
					</Typography>
					<TextField
						autoFocus
						margin="dense"
						id="reason"
						label="Reason for Request"
						type="text"
						fullWidth
						multiline
						rows={4}
						variant="outlined"
						value={requestReason}
						onChange={(e) => setRequestReason(e.target.value)}
						placeholder="Explain why you need access to Discord bots and what you plan to use them for..."
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleRequestClose} disabled={requestLoading}>
						Cancel
					</Button>
					<Button
						onClick={handleRequestSubmit}
						variant="contained"
						color="primary"
						startIcon={<Send />}
						disabled={requestLoading || !requestReason.trim()}
					>
						{requestLoading ? "Sending..." : "Send Request"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default BotList;
